if (!customElements.get('recommendation-offer')) {
  class RecommendationOffer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try {
        this.data = JSON.parse(this.querySelector('[data-rec-data]').textContent);
      } catch (error) {
        return;
      }
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.cards = new Map(Array.from(this.querySelectorAll('[data-rec-variant]'), (card) => [card.dataset.recVariant, card]));
      this.section = this.closest('.shopify-section');
      const sync = () => this.update();
      this.section?.addEventListener('on:variant:change', sync, { signal });
      this.section?.addEventListener('change', (event) => {
        if (event.target.name === 'id') sync();
      }, { signal });
      // Radio-like: one card at a time, clicking the selected card again clears it. Links keep navigating.
      this.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        const card = event.target.closest('[data-rec-variant]');
        if (card) this.select(this.selected === card.dataset.recVariant ? null : card.dataset.recVariant);
      }, { signal });
      this.list = this.querySelector('.oh-rec-offer__list');
      this.dots = this.querySelector('[data-rec-dots]');
      this.list.addEventListener('scroll', () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => this.updateScroll());
      }, { passive: true, signal });
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.renderedKey = null;
    }

    select(variantId) {
      this.selected = variantId;
      this.cards.forEach((card, id) => {
        const active = id === variantId;
        card.classList.toggle('is-selected', active);
        card.querySelector('[data-rec-select]').setAttribute('aria-pressed', String(active));
      });
      // product-form.js adds getSelectedPrice() to the Add to Cart label price.
      document.getElementById(this.dataset.formId)?.closest('product-form')?.scheduleAtcPriceUpdate?.();
    }

    // Displayed (discounted) price of the selected, visible card in cents; 0 when nothing is selected.
    getSelectedPrice() {
      const card = this.selected && this.cards.get(this.selected);
      return card && !card.hidden && !this.hidden ? Number(card.dataset.recPrice) || 0 : 0;
    }

    // Selected variant's list (precomputed in Liquid, variant metafield or product fallback); initial list when nothing is selected.
    update() {
      const id = document.getElementById(this.dataset.formId)?.querySelector('[name="id"]')?.value;
      const ids = (id && this.data.variants[id]) || this.data.initial;
      const key = ids.join(',');
      if (key === this.renderedKey) return;
      this.renderedKey = key;
      this.cards.forEach((card) => { card.hidden = true; });
      ids.forEach((variantId, index) => {
        const card = this.cards.get(String(variantId));
        if (!card) return;
        const badge = card.querySelector('[data-rec-badge]');
        const label = this.data.labels[index] || '';
        badge.textContent = label;
        badge.hidden = !label;
        card.style.order = index + 1;
        card.hidden = false;
      });
      if (this.selected && this.cards.get(this.selected)?.hidden) this.select(null);
      this.hidden = ids.length === 0;
      this.visibleCards = ids.map((variantId) => this.cards.get(String(variantId))).filter(Boolean);
      this.dots.replaceChildren(...this.visibleCards.map(() => document.createElement('span')));
      this.dots.hidden = this.visibleCards.length < 2;
      this.list.scrollLeft = 0;
      this.updateScroll();
    }

    // Mobile carousel state: edge fades (data-at-start / data-at-end) and the active dot.
    updateScroll() {
      this.scrollFrame = 0;
      const { scrollLeft, clientWidth, scrollWidth } = this.list;
      this.list.toggleAttribute('data-at-start', scrollLeft <= 4);
      this.list.toggleAttribute('data-at-end', scrollLeft + clientWidth >= scrollWidth - 4);
      if (!this.visibleCards?.length) return;
      const start = this.list.getBoundingClientRect().left;
      let active = 0;
      let best = Infinity;
      this.visibleCards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - start);
        if (distance < best) {
          best = distance;
          active = index;
        }
      });
      if (this.list.hasAttribute('data-at-end')) active = this.visibleCards.length - 1;
      [...this.dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    }

    // Called by product-form.js on the main Add to Cart; the item is added in the same request as the main product.
    getItem(form) {
      if (form.getAttribute('id') !== this.dataset.formId || this.hidden) return null;
      const card = this.selected && this.cards.get(this.selected);
      if (!card || card.hidden) return null;
      return {
        id: Number(this.selected),
        properties: {
          _ornate_rec_offer: 'true',
          _ornate_rec_discount: String(this.data.discount),
          _ornate_parent_variant: String(form.querySelector('[name="id"]')?.value || '')
        }
      };
    }
  }
  customElements.define('recommendation-offer', RecommendationOffer);
}
